import React, { useState } from "react";
import { TextField, Typography, Box, Alert } from "@mui/material";

interface SchedulingIntegrationFormProps {
  data?: any;
  onChange: (data: any) => void;
  onValidationChange?: (isValid: boolean) => void;
}

const SchedulingIntegrationForm: React.FC<SchedulingIntegrationFormProps> = ({
  data = {},
  onChange,
  onValidationChange,
}) => {
  const [formData, setFormData] = useState({
    scheduleUrl: data.scheduleUrl || "",
  });

  React.useEffect(() => {
    // Validate that the URL is present and looks like a valid URL
    const isValidUrl = formData.scheduleUrl.length > 0 &&
      (formData.scheduleUrl.startsWith("http://") || formData.scheduleUrl.startsWith("https://"));

    if (onValidationChange) {
      onValidationChange(isValidUrl);
    }
  }, [formData, onValidationChange]);

  const handleChange = (field: string, value: string) => {
    const newData = { ...formData, [field]: value };
    setFormData(newData);
    onChange(newData);
  };

  return (
    <>
      <Box sx={{ mb: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Connect your calendar scheduling tool (like MeetQuick, Calendly, Cal.com, etc.)
          to track when email recipients book meetings through your scheduling links.
        </Typography>
      </Box>
      <TextField
        fullWidth
        margin="normal"
        label="Scheduling Page URL"
        value={formData.scheduleUrl}
        onChange={(e) => handleChange("scheduleUrl", e.target.value)}
        placeholder="https://cal.com/username/meeting-type"
        helperText="Enter the full URL to your scheduling page. This will be tracked when included in sequence emails."
        required
      />
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>
          Supported scheduling tools with full booking tracking:
        </Typography>
        <Typography variant="body2" component="ul" sx={{ m: 0, pl: 2 }}>
          <li><strong>Cal.com</strong> - e.g., https://cal.com/username/meeting</li>
          <li><strong>Calendly</strong> - e.g., https://calendly.com/username/meeting</li>
          <li><strong>MeetQuick</strong> - e.g., https://meetquick.app/schedule/username/meeting</li>
        </Typography>
        <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
          These links will be embedded directly in the tracking page, allowing us to detect when meetings are booked and notify you.
        </Typography>
      </Alert>
    </>
  );
};

export default SchedulingIntegrationForm;
